import { AdministerMerchant, getMerchant, updateMerchantPayoutStatus } from 'APIs/merchants/merchants';
import { AdministerMerchantRequest } from 'APIs/merchants/merchants.administer.types';
import { PayoutDetails, PayoutStatusUpdateRequest } from 'APIs/merchants/merchants.types';
import { useCreateMerchantContext } from 'Components/ManageMerchant/context';
import Message from 'Components/Message';
import Spinner from 'Components/Spinner/Spinner';
import { H2, Label, LpBox, LpDetails, Row } from 'Constants/styles';
import { MouseEvent, ReactElement, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import getErrorMessage from 'utils/getErrorMessage';

import { FormControl, SpinnerWrapper } from './styles';

type Props = {
  merchantId: string;
};

const initPayoutDetails: PayoutDetails = {
  payoutSettings: {
    notificationEmails: [],
    notificationsEnabled: false,
  },
  payoutSchedule: 'monthly',
  payoutDelayDays: 0,
  payoutExpediteNonBusinessDays: false,
  automatedPayoutsEnabled: false,
  payoutStatus: 'Disabled',
};

const PayoutSettings = ({ merchantId }: Props): ReactElement => {
  const { merchantStatus, setMerchantStatus } = useCreateMerchantContext();

  const { apiBaseUri, apiAprilBaseUri } = useSelector((state: ReduxStateType) => ({
    apiBaseUri: state.apiBaseUri,
    apiAprilBaseUri: state.apiAprilBaseUri,
  }));

  const [payoutDetails, setPayoutDetails] = useState<PayoutDetails>(initPayoutDetails);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPayoutStatusUpdating, setIsPayoutStatusUpdating] = useState<boolean>(false);
  const [isPayoutDelayUpdating, setIsPayoutDelayUpdating] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');

  const fetchMerchantDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      const merchantResp = await getMerchant(apiAprilBaseUri, merchantId);
      setPayoutDetails(merchantResp.payoutDetails);
    } catch (e) {
      setErrorMsg(getErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  }, [apiAprilBaseUri, merchantId]);

  useEffect(() => {
    if (!apiAprilBaseUri || !merchantId) {
      return;
    }
    fetchMerchantDetails();
  }, [apiAprilBaseUri, fetchMerchantDetails, merchantId]);

  const updatePayoutStatus = async (value: string) => {
    try {
      setPayoutDetails((prev) => ({ ...prev, payoutStatus: value as 'Enabled' | 'Disabled' }));
      if (merchantStatus) {
        setMerchantStatus({
          ...merchantStatus,
          payoutStatus: value as 'Enabled' | 'Disabled',
        });
      }
      setIsPayoutStatusUpdating(true);
      const payload = {
        payoutStatus: value,
      };
      await updateMerchantPayoutStatus(apiBaseUri, merchantId, payload as PayoutStatusUpdateRequest);
      toast.success('Payout status updated successfully');
    } catch (e) {
      toast.error(`Error: ${getErrorMessage(e)}`);
    } finally {
      setIsPayoutStatusUpdating(false);
    }
  };

  const updatePayoutDelay = async (value: string) => {
    try {
      const days = parseInt(value, 10);
      if (!Number.isInteger(days) || days < 1 || days > 4) {
        toast.error('Invalid payout delay value. Please select a value between 1 and 4 days.');
        return;
      }
      setPayoutDetails((prev) => ({ ...prev, payoutDelayDays: days }));
      setIsPayoutDelayUpdating(true);
      const payoutDelayMapping: Record<number, string> = {
        4: 'fourdays',
        3: 'threedays',
        2: 'twodays',
        1: 'oneday',
      };
      const payoutDelay = payoutDelayMapping[days] || 'oneday';
      const payload = {
        UpdatePayoutDelay: {
          payoutDelay,
          payoutExpediteNonBusinessDays: payoutDetails.payoutExpediteNonBusinessDays,
        },
      };
      await AdministerMerchant(apiAprilBaseUri, merchantId, payload as AdministerMerchantRequest);
      toast.success('Payout delay updated successfully');
    } catch (e) {
      toast.error(`Error: ${getErrorMessage(e)}`);
    } finally {
      setIsPayoutDelayUpdating(false);
    }
  };

  const handleCloseMsg = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setErrorMsg('');
  };

  return (
    <>
      {isLoading && (
        <SpinnerWrapper>
          <Spinner color="#0016D1" />
        </SpinnerWrapper>
      )}
      {!isLoading && errorMsg.length > 0 && (
        <Message className="mt-2 mb-0" description={errorMsg} success={false} handleClose={handleCloseMsg} />
      )}
      {!isLoading && (
        <LpBox>
          <Row>
            <div className="col-sm-7">
              <H2 className="mb-0">Payout settings</H2>
            </div>
          </Row>
          <LpDetails>
            <Row className="pt-4">
              <div className="col-sm-4">
                <Label>Status</Label>
              </div>
              <div className="col-sm-8">
                <FormControl
                  value={payoutDetails.payoutStatus}
                  onChange={(e) => updatePayoutStatus(e.target.value)}
                  data-testid="payout-status-select"
                  disabled={isPayoutStatusUpdating}
                >
                  <option key={`Enabled`} value={`Enabled`}>
                    Enabled
                  </option>
                  <option key={`Disabled`} value={`Disabled`}>
                    Disabled
                  </option>
                </FormControl>
              </div>
            </Row>
            <Row className="pt-4">
              <div className="col-sm-4">
                <Label>Payout Schedule</Label>
              </div>
              <div className="col-sm-8">{payoutDetails.payoutSchedule}</div>
            </Row>
            <Row className="pt-4">
              <div className="col-sm-4">
                <Label>Notifications</Label>
              </div>
              <div className="col-sm-8">
                {payoutDetails.payoutSettings.notificationsEnabled ? 'Enabled' : 'Disabled'}
              </div>
            </Row>
            <Row className="pt-4">
              <div className="col-sm-4">
                <Label>Notification emails</Label>
              </div>
              <div className="col-sm-8">
                {payoutDetails.payoutSettings.notificationEmails.length > 0
                  ? payoutDetails.payoutSettings.notificationEmails.concat(',')
                  : '-'}
              </div>
            </Row>
            <Row className="pt-4">
              <div className="col-sm-4">
                <Label>Delay</Label>
              </div>
              <div className="col-sm-8">
                <FormControl
                  value={payoutDetails.payoutDelayDays}
                  onChange={(e) => updatePayoutDelay(e.target.value)}
                  data-testid="payout-delay-select"
                  disabled={isPayoutDelayUpdating}
                >
                  <option key={`one`} value={`1`}>
                    One day
                  </option>
                  <option key={`two`} value={`2`}>
                    Two days
                  </option>
                  <option key={`three`} value={`3`}>
                    Three days
                  </option>
                  <option key={`four`} value={`4`}>
                    Four days
                  </option>
                </FormControl>
              </div>
            </Row>
          </LpDetails>
        </LpBox>
      )}
    </>
  );
};

export default PayoutSettings;
